import React, { useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link } from 'react-router-dom';
import history from '../../../../../history';
import { deleteAssestment, getAssementByIdAndRole, getAssestmentsByRole } from '../../../../Constants/Api/functions/function';
import { setAllAssessments } from '../../../../Reducers/allAssessmentsReducer';

// Quill
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';

import {
    Button,
    Divider,
    Popconfirm,
    Spin,
    notification,
    Tabs,
    Tag,
    Space,
    Card
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    ArrowLeftOutlined,
    BookOutlined,
    TeamOutlined,
    CalendarOutlined
} from '@ant-design/icons';

import { getAssessmentTypeDetails, calculateCourseAssessmentBreakdown } from '../../../../Utils/gradeUtils';

import './style.scss';
import EditAssessment from './EditAssessment';
import Submission from './Submission';
import SubmitAssessment from './SubmitAssessment';

const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
        const dateObj = new Date(dateStr);
        if (isNaN(dateObj.getTime())) return dateStr;
        return dateObj.toLocaleString([], { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch {
        return dateStr;
    }
};

const CountdownTimer = ({ endDate }) => {
    const [timeLeft, setTimeLeft] = useState(null);

    useEffect(() => {
        if (!endDate) return;

        const calculateTimeLeft = () => {
            const difference = new Date(endDate) - new Date();
            let timeLeftData = {};

            if (difference > 0) {
                timeLeftData = {
                    days: Math.floor(difference / (1000 * 60 * 60 * 24)),
                    hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
                    minutes: Math.floor((difference / 1000 / 60) % 60),
                    seconds: Math.floor((difference / 1000) % 60),
                    isUrgent: difference < 1000 * 60 * 60 * 24, // Less than 24 hours
                    expired: false
                };
            } else {
                timeLeftData = { expired: true };
            }
            setTimeLeft(timeLeftData);
        };

        calculateTimeLeft();
        const interval = setInterval(calculateTimeLeft, 1000);

        return () => clearInterval(interval);
    }, [endDate]);

    if (!timeLeft) return null;

    if (timeLeft.expired) {
        return (
            <div className="countdown-expired">
                ⚠️ Deadline Passed
            </div>
        );
    }

    return (
        <div className={`countdown-container ${timeLeft.isUrgent ? 'urgent' : ''}`}>
            <div className="countdown-title">⏳ Time Remaining:</div>
            <div className="countdown-value">
                {timeLeft.days > 0 && <span className="time-part"><strong>{timeLeft.days}</strong>d </span>}
                <span className="time-part"><strong>{timeLeft.hours}</strong>h </span>
                <span className="time-part"><strong>{timeLeft.minutes}</strong>m </span>
                <span className="time-part"><strong>{timeLeft.seconds}</strong>s </span>
            </div>
        </div>
    );
};

const Assessment = () => {
    const [loading, setLoading] = useState(false);
    const [openEdit, setOpenEdit] = useState(false);
    const { id: assessmentId } = useParams();
    const [api, contextHolder] = notification.useNotification();
    const dispatch = useDispatch();
    const allAssessments = useSelector(state => state.allAssessments.allAssessments);
    const userProfile = useSelector(state => state.userProfile.userProfile);
    const [assessmentData, setAssessmentData] = useState();

    const isStudent = userProfile?.role === 'student';

    useEffect(() => {
        if (userProfile?.role && userProfile?.id && (!allAssessments || allAssessments.length === 0)) {
            getAssestmentsByRole(userProfile.role, userProfile.id).then(res => {
                if (res) dispatch(setAllAssessments(res));
            }).catch(console.error);
        }
    }, [userProfile?.role, userProfile?.id]);

    const courseAssessments = useMemo(() => {
        if (!assessmentData?.courseId) return [];
        const fromAll = (allAssessments || []).filter(
            (a) => String(a.courseId) === String(assessmentData.courseId)
        );
        if (fromAll.length > 0) return fromAll;
        return [assessmentData];
    }, [allAssessments, assessmentData]);

    const courseBreakdown = useMemo(() => {
        return calculateCourseAssessmentBreakdown(courseAssessments);
    }, [courseAssessments]);

    const caEligibility = courseBreakdown?.caEligibility;

    useEffect(() => {
        if (allAssessments?.length) {
            const assessment = allAssessments?.find(a => String(a.id) === String(assessmentId));
            if (assessment) {
                setAssessmentData(assessment);
            } else {
                handleGetAssessment();
            }
        } else {
            handleGetAssessment();
        }
    }, [allAssessments, assessmentId]);

    const handleGetAssessment = async () => {
        setLoading(true);
        try {
            const response = await getAssementByIdAndRole(assessmentId, userProfile?.role, userProfile?.id);
            setAssessmentData(response);
        } catch (error) {
            console.error('getAssementByIdAndRole', error);
        }
        setLoading(false);
    };

    const handleDeleteAssessment = async () => {
        try {
            const response = await deleteAssestment(assessmentId);
            if (response && (response.status === 200 || response.success)) {
                openNotification('success', 'Assessment deleted successfully', '');
                setTimeout(() => {
                    history.push('/dashboard/assessments');
                }, 1000);
            } else {
                openNotification('error', 'Failed to delete assessment', '');
            }
        } catch (error) {
            console.error('handleDeleteAssessment', error);
            openNotification('error', 'Failed to delete assessment', '');
        }
    };

    const openNotification = (type, message, description) => {
        api[type]({
            message: message,
            description: description,
        });
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh', width: '100%' }}>
                <Spin tip="Loading Assessment..." size="large" />
            </div>
        );
    }

    return (
        <div style={{ width: '100%' }}>
            {contextHolder}

            {/* Back Navigation */}
            <div style={{ marginBottom: 16 }}>
                <Link to="/dashboard/assessments">
                    <Button type="link" icon={<ArrowLeftOutlined />} style={{ padding: 0, fontSize: 14 }}>
                        Back to Assessments
                    </Button>
                </Link>
            </div>

            {/* ADMIN & INSTRUCTOR VIEW: Structured Dashboard */}
            {!isStudent ? (
                <div className="assessment--admin">
                    {/* Header Card */}
                    <div className="admin-header-card">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
                            <div>
                                <Space size={8} wrap style={{ marginBottom: 8 }}>
                                    <Tag color={getAssessmentTypeDetails(assessmentData?.assessmentType).tagColor} style={{ fontSize: 13, padding: '2px 10px', fontWeight: 600 }}>
                                        {getAssessmentTypeDetails(assessmentData?.assessmentType).label}
                                    </Tag>
                                    {assessmentData?.courseName && (
                                        <Tag color="geekblue" style={{ fontSize: 13, padding: '2px 10px', fontWeight: 600 }}>
                                            {assessmentData?.courseName}
                                        </Tag>
                                    )}
                                    {assessmentData?.courseCode && (
                                        <Tag color="blue">{assessmentData.courseCode}</Tag>
                                    )}
                                    {assessmentData?.isPublished ? (
                                        <Tag color="green">Published</Tag>
                                    ) : (
                                        <Tag color="default">Draft</Tag>
                                    )}
                                </Space>
                                <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: '#0f172a' }}>
                                    {assessmentData?.name}
                                </h1>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginTop: 10, flexWrap: 'wrap', color: '#64748b', fontSize: 13 }}>
                                    <span>
                                        <CalendarOutlined style={{ marginRight: 6 }} />
                                        Due Date: <strong>{formatDate(assessmentData?.endDate)}</strong>
                                    </span>
                                </div>
                            </div>

                            {/* Actions & Countdown */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                                <CountdownTimer endDate={assessmentData?.endDate} />

                                {['superadmin', 'admin', 'instructor'].includes(userProfile?.role) && (
                                    <Space size={8}>
                                        <Button
                                            type="default"
                                            icon={<EditOutlined />}
                                            onClick={() => setOpenEdit(true)}
                                        >
                                            Edit
                                        </Button>
                                        <Popconfirm
                                            title="Are you sure to delete this assessment?"
                                            onConfirm={handleDeleteAssessment}
                                            okText="Yes"
                                            cancelText="No"
                                            okButtonProps={{ danger: true }}
                                        >
                                            <Button
                                                danger
                                                icon={<DeleteOutlined />}
                                            >
                                                Delete
                                            </Button>
                                        </Popconfirm>
                                    </Space>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Admin Tabs: Submissions & Grading vs Assignment Brief */}
                    <div className="admin-tabs-card">
                        <Tabs
                            defaultActiveKey="submissions"
                            size="large"
                            items={[
                                {
                                    key: 'submissions',
                                    label: (
                                        <Space size={6}>
                                            <TeamOutlined />
                                            <span>Submissions & Grading</span>
                                        </Space>
                                    ),
                                    children: (
                                        <Submission
                                            assessmentId={assessmentId}
                                            assessmentData={assessmentData}
                                        />
                                    ),
                                },
                                {
                                    key: 'brief',
                                    label: (
                                        <Space size={6}>
                                            <BookOutlined />
                                            <span>Assignment Brief & Resources</span>
                                        </Space>
                                    ),
                                    children: (
                                        <div style={{ padding: '12px 4px' }}>
                                            <Card title="Task Description & Guidelines" style={{ borderRadius: 8, marginBottom: 20 }}>
                                                <ReactQuill
                                                    className='quill'
                                                    readOnly
                                                    theme="bubble"
                                                    value={assessmentData?.description || '<em>No description provided.</em>'}
                                                />
                                            </Card>

                                            {assessmentData?.assessmentUrl && (
                                                <Card title="Resource Materials & Reference Link" style={{ borderRadius: 8 }}>
                                                    <a
                                                        href={assessmentData?.assessmentUrl}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                        style={{ fontSize: 14, wordBreak: 'break-all' }}
                                                    >
                                                        {assessmentData?.assessmentUrl}
                                                    </a>
                                                </Card>
                                            )}
                                        </div>
                                    ),
                                }
                            ]}
                        />
                    </div>
                </div>
            ) : (
                /* STUDENT VIEW: Side-by-side with Submission form on the right */
                <div className='assessment'>
                    <div className='left'>
                        <div className='hedaer'>
                            <Space size={8} wrap style={{ marginBottom: 8 }}>
                                <Tag color={getAssessmentTypeDetails(assessmentData?.assessmentType).tagColor} style={{ fontSize: 13, padding: '2px 10px', fontWeight: 600 }}>
                                    {getAssessmentTypeDetails(assessmentData?.assessmentType).label}
                                </Tag>
                                {assessmentData?.courseName && (
                                    <Tag color="geekblue" style={{ fontSize: 13, padding: '2px 10px', fontWeight: 600 }}>
                                        {assessmentData?.courseName}
                                    </Tag>
                                )}
                            </Space>
                            <h1>
                                {assessmentData?.name}
                            </h1>
                        </div>

                        <div className='body'>
                            <h2>
                                Due date: <span>{formatDate(assessmentData?.endDate)}</span>
                            </h2>
                            <CountdownTimer endDate={assessmentData?.endDate} />
                            <Divider />
                            <h2>
                                Description:
                            </h2>
                            <ReactQuill
                                className='quill'
                                readOnly
                                theme="bubble"
                                value={assessmentData?.description}
                            />
                            <Divider />
                            <h2>
                                Resource materials: <a
                                    href={assessmentData?.assessmentUrl}
                                    target='_blank'
                                    rel='noreferrer'
                                >
                                    {assessmentData?.assessmentUrl}
                                </a>
                            </h2>
                        </div>
                    </div>

                    <div className='right'>
                        <SubmitAssessment
                            assessmentData={assessmentData}
                            handleGetAssessment={handleGetAssessment}
                            userProfile={userProfile}
                            contextHolder={contextHolder}
                            api={api}
                            caEligibility={caEligibility}
                        />
                    </div>
                </div>
            )}

            <EditAssessment
                assessmentData={assessmentData}
                isOpen={openEdit}
                setOpen={setOpenEdit}
                handleGetAssessment={handleGetAssessment}
            />
        </div>
    );
};

export default Assessment;